import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';

const CakesContext = createContext(null);

export const useCakes = () => {
  const context = useContext(CakesContext);
  if (!context) {
    throw new Error('useCakes must be used within a CakesProvider');
  }
  return context;
};

export const CakesProvider = ({ children }) => {
  const [cakes, setCakes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastFetched, setLastFetched] = useState(null);

  // Track in-flight fetch to prevent duplicate requests
  const fetchPromiseRef = useRef(null);

  // Cache TTL in milliseconds - configurable via environment variable
  // Default: 15 minutes (900000 ms)
  const CACHE_TTL_MS = parseInt(process.env.REACT_APP_CAKES_CACHE_TTL_MS || '900000', 10);

  const fetchCakes = useCallback(async (force = false) => {
    // Skip if we have cached data and it's less than TTL old (unless forced)
    if (!force && cakes.length > 0 && lastFetched && (Date.now() - lastFetched) < CACHE_TTL_MS) {
      return cakes;
    }

    // Return existing fetch promise if one is already in progress (deduplication)
    if (fetchPromiseRef.current) {
      return fetchPromiseRef.current;
    }

    setLoading(true);
    setError(null);

    // Create the fetch promise
    const promise = (async () => {
      try {
        const baseUrl = process.env.REACT_APP_API_BASE_URL;
        const response = await fetch(`${baseUrl}/cakes`);

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();
        // Handle Lambda proxy response format
        const data = typeof result.body === 'string' ? JSON.parse(result.body) : result;

        setCakes(data);
        setLastFetched(Date.now());
        return data;
      } catch (e) {
        console.error('Failed to fetch cakes:', e);
        setError(e.message || 'Failed to fetch');
        throw e;
      } finally {
        setLoading(false);
        fetchPromiseRef.current = null;
      }
    })();

    fetchPromiseRef.current = promise;
    return promise;
  }, [cakes, lastFetched, CACHE_TTL_MS]);

  // Initial fetch on mount
  useEffect(() => {
    fetchCakes();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Update a single cake in the cache (used after editing metadata)
  const updateCake = useCallback((id, updatedData) => {
    setCakes(prevCakes =>
      prevCakes.map(cake =>
        cake.id === id
          ? {
              ...cake,
              ...updatedData,
              name: updatedData.title || cake.name,
              tags: Array.isArray(updatedData.tags)
                ? updatedData.tags
                : (updatedData.tags ? updatedData.tags.split(',').map(t => t.trim()) : cake.tags)
            }
          : cake
      )
    );
  }, []);

  // Invalidate cache and refetch
  const refreshCakes = useCallback(() => {
    setLastFetched(null);
    return fetchCakes(true);
  }, [fetchCakes]);

  // Get cakes filtered for carousel (showInCarousel = true or undefined for backward compatibility)
  // Randomly shuffles if more cakes exist than limit (prevents showing same images)
  const getCarouselCakes = useCallback((limit = 10) => {
    const carouselCakes = cakes.filter(
      cake => cake.showInCarousel === true || cake.showInCarousel === undefined
    );

    // If we have more cakes than limit, randomly shuffle and select
    if (carouselCakes.length > limit) {
      const shuffled = [...carouselCakes].sort(() => Math.random() - 0.5);
      return shuffled.slice(0, limit);
    }

    return carouselCakes.slice(0, limit);
  }, [cakes]);

  // Get all unique tags from cakes
  const getAllTags = useCallback(() => {
    const uniqueTags = new Set(['All']);
    cakes.forEach(cake => {
      if (cake.tags && Array.isArray(cake.tags)) {
        cake.tags.forEach(tag => uniqueTags.add(tag));
      }
    });
    return Array.from(uniqueTags);
  }, [cakes]);

  const value = {
    cakes,
    loading,
    error,
    fetchCakes,
    updateCake,
    refreshCakes,
    getCarouselCakes,
    getAllTags,
  };

  return (
    <CakesContext.Provider value={value}>
      {children}
    </CakesContext.Provider>
  );
};

export default CakesContext;
